import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ImageProps } from '../../../prop-types';
import NavigationMenuItem from './menu-item';
import SearchBar from '../search-bar';
import CountrySwitch from '../country-switch';
import { Overlay } from '../../styled/overlay';
import { MobileMenuToggle } from '../buttons';
import {
  NavigationWrapper,
  Menus,
  MenuList,
  AdditionalMenu,
  Item,
  ItemLink,
  NavigationHeader,
} from './styles';
import { Logo, LogoWrapper } from '../logo';
import { sendEvent } from '../../../utils/analytics';

const handleNavWithChildren = (navigationItems) => {
  let primaryNav;
  let navItemsRest;
  let primaryNavId;
  if (Array.isArray(navigationItems)) {
    const firstNav = navigationItems.slice(0, 1)[0];
    if (
      // check if first nav element has childNavigationItems
      Object.prototype.hasOwnProperty.call(firstNav, 'childNavigationItems') &&
      firstNav.childNavigationItems !== null
    ) {
      // extract the first link with children as primary (split button) nav
      [primaryNav, ...navItemsRest] = navigationItems;
      primaryNavId = primaryNav.id || primaryNav.contentful_id;
    } else {
      // else if no children, all buttons are equal
      navItemsRest = navigationItems;
    }

    // Prevents multiple nav items being styled as active
    // e.g. both "Support us" and "Shops" being active on the shops page as both sit under /support_us
    const activeNavItems = navItemsRest.filter(
      (navItem) => navItem?.navigationLink?.[0]?.navigation?.active
    );

    if (activeNavItems.length > 0) {
      activeNavItems.sort(
        (a, b) =>
          /* eslint-disable no-unsafe-optional-chaining */
          b?.navigationLink?.[0]?.navigation?.path.length -
          /* eslint-disable no-unsafe-optional-chaining */
          a?.navigationLink?.[0]?.navigation?.path.length
      );

      const indexOfIntendedActiveNav = navItemsRest.findIndex(
        (navItem) =>
          navItem?.navigationLink?.[0]?.navigation?.path ===
          activeNavItems[0]?.navigationLink?.[0]?.navigation?.path
      );

      navItemsRest[
        indexOfIntendedActiveNav
      ].navigationLink[0].navigation.active = true;

      activeNavItems.slice(1).forEach((currentlyActiveNavItem) => {
        const indexOfNavItemToDisable = navItemsRest.findIndex(
          (navItem) =>
            navItem?.navigationLink?.[0]?.navigation?.path ===
            currentlyActiveNavItem?.navigationLink?.[0]?.navigation?.path
        );

        navItemsRest[
          indexOfNavItemToDisable
        ].navigationLink[0].navigation.active = false;
      });
    }
  }
  return { primaryNav, primaryNavId, navItemsRest };
};

const Navigation = ({
  pageData,
  overlayMenuOpen,
  setOverlayMenuOpen,
  siblingSiteUrl,
  siblingSiteName,
  isDesktop,
  logo,
  logoMobile,
  mobileMenuToggleOpenRef,
  mobileMenuToggleClosedRef,
}) => {
  const { navigationItems, additionalLink } = pageData;
  const [activeSubMenu, setActiveSubMenu] = useState(false);

  const updateActiveSubMenu = useCallback(
    (id) => {
      if (id === activeSubMenu) {
        setActiveSubMenu(false);
      } else {
        setActiveSubMenu(id);
      }
    },
    [activeSubMenu]
  );

  // Handling for first navigation item having childNavigationItems
  const { primaryNav, primaryNavId, navItemsRest } =
    handleNavWithChildren(navigationItems);

  return (
    <>
      <Overlay
        $active={overlayMenuOpen}
        data-testid="navigation-overlay"
        onClick={() => {
          if (isDesktop) {
            updateActiveSubMenu();
            setOverlayMenuOpen(false);
          } else {
            setOverlayMenuOpen(false);
          }
        }}
      />
      <NavigationWrapper
        $active={overlayMenuOpen}
        data-testid="navigation-wrapper"
        onBlur={(e) => {
          const { currentTarget } = e;
          requestAnimationFrame(() => {
            // Check if the new focused element is a child of the mobile menu, if not then close it
            if (!currentTarget.contains(document.activeElement)) {
              setOverlayMenuOpen(false);
            }
          });
        }}
      >
        {/* title area with logo and search for the mobile drawer */}
        {!isDesktop && (
          <>
            <NavigationHeader>
              <LogoWrapper to="/" data-logoplacement="header">
                <Logo logo={logo} logoMobile={logoMobile} />
              </LogoWrapper>
              <MobileMenuToggle
                $active={overlayMenuOpen}
                ref={mobileMenuToggleOpenRef}
                onClick={() => {
                  setOverlayMenuOpen(false);
                  mobileMenuToggleClosedRef?.current.focus();
                  updateActiveSubMenu();
                  sendEvent({
                    event: 'headerMenu',
                    menuAction: overlayMenuOpen ? 'Close' : 'Open',
                  });
                }}
              />
            </NavigationHeader>
            <SearchBar placement="mobileDrawer" />
          </>
        )}

        {/* main menu navigation loop */}
        <Menus aria-label="Main menu">
          {navigationItems && (
            <MenuList aria-hidden="false">
              {/* extracted primary nav item */}
              {primaryNav !== undefined ? (
                <NavigationMenuItem
                  key={primaryNavId}
                  id={primaryNavId}
                  menuItem={primaryNav}
                  overlayMenuOpen={activeSubMenu === primaryNavId}
                  setOverlayMenuOpen={setOverlayMenuOpen}
                  setActiveSubMenu={() => updateActiveSubMenu(primaryNavId)}
                  isDesktop={isDesktop}
                  primaryNav
                />
              ) : null}

              {navItemsRest.map((item) => {
                // NOTE: donate menu item skipped on desktop, button presented instead
                if (item?.navigationLink?.[0]?.slug === 'donate' && isDesktop) {
                  return;
                }

                const itemId = item.id || item.contentful_id;

                return (
                  <NavigationMenuItem
                    key={itemId}
                    id={itemId}
                    menuItem={item}
                    overlayMenuOpen={activeSubMenu === itemId}
                    setActiveSubMenu={() => updateActiveSubMenu(itemId)}
                    isDesktop={isDesktop}
                  />
                );
              })}
            </MenuList>
          )}

          {/* secondary menu navigation loop */}
          {additionalLink && (
            <AdditionalMenu role="navigation" aria-label="Secondary menu">
              {additionalLink.map((item) => {
                const itemId = item.id || item.contentful_id;
                if (item.__typename === 'ContentfulComponentNavigationItem') {
                  return (
                    <NavigationMenuItem
                      key={itemId}
                      id={itemId}
                      menuItem={item}
                      overlayMenuOpen={activeSubMenu === itemId}
                      setActiveSubMenu={() => updateActiveSubMenu(itemId)}
                      isDesktop={isDesktop}
                    />
                  );
                }
                return (
                  <Item key={itemId}>
                    <ItemLink link={item}>
                      {item.menuLabel || item.title}
                    </ItemLink>
                  </Item>
                );
              })}
            </AdditionalMenu>
          )}

          {!isDesktop && (
            <CountrySwitch
              siblingSiteUrl={siblingSiteUrl}
              siblingSiteName={siblingSiteName}
            />
          )}
        </Menus>
      </NavigationWrapper>
    </>
  );
};

Navigation.propTypes = {
  overlayMenuOpen: PropTypes.bool.isRequired,
  setOverlayMenuOpen: PropTypes.func,
  pageData: PropTypes.shape({
    navigationItems: PropTypes.arrayOf(PropTypes.object),
    additionalLink: PropTypes.arrayOf(
      PropTypes.shape({
        menuLabel: PropTypes.string,
        title: PropTypes.string,
      })
    ),
  }),
  siblingSiteName: PropTypes.string.isRequired,
  siblingSiteUrl: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
  logo: ImageProps.isRequired,
  logoMobile: ImageProps.isRequired,
  mobileMenuToggleOpenRef: PropTypes.object,
  mobileMenuToggleClosedRef: PropTypes.object,
};

export default Navigation;
